// pgw 07-dec-2025 includes changes

import React from 'react';
import { MixBoard, ChoirConductor, Modal, Input, Button } from "player-components";
import audioEngine from "../../services/audioEngine";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTour, TourProvider } from "@reactour/tour";
import formatDuration from 'date-fns/formatDuration'

import {
  selectStatus,
  selectStems,
  selectUserStems,
  unmuteChannel,
  muteChannel,
  removeChannelAsync,
  soloChannel,
  unsoloChannel,
  selectLoadedSong,
  selectSpeed,
  selectDuration,
  selectPitch,
  changeChannelGain,
  changeChannelPan,
  selectLoadingProgress,
  selectIsSafari,
  selectIsFirefox,
  addNewStem,
  disarmChannel,
  armChannel,
  attachUserMicrophoneAsync,
  mountSessionAsync,
  stopSong,
  packSessionAsync,
  mountSessionAndRenderOfflineAsync,
  selectSession,
  resetSessionMixerSettings,
  selectIntroLevel,
  selectClick1Level,
  selectClick2Level,
  selectCueLevel,
  setIntroLevel,
  setClick1Level,
  setClick2Level,
  setCueLevel,
  openSession,
  updateSessionMetadata,
  seek,
  pauseSong,
  unmountSessionAsync
} from "../../features/session/sessionSlice";


import {
  saveMetadataOverride,
  deleteMetadataOverride,
  chaptersToXml,
  lyricsToXml,
  downloadTextFile,
} from "../../utils/metadataOverrides";


import {fetchLyricsAsync, selectLyricsStatus} from '../../features/lyrics/lyricsSlice'

import LoadingScreen from "../../components/LoadingScreen";
import {
  saveExistingSession,
  selectSetlistSessions,
  resetLastCreated
} from "../../features/setlist/setlistSlice";
import NoSessionLoaded from "../../components/NoSessionLoaded";
import { ExclamationIcon, MicrophoneIcon } from "@heroicons/react/outline";
import {
  addNotificationAsync,
  selectNewTrackModalVisible,
  setNewTrackModalVisible,
  selectClientConfig,
  selectShowLyrics,
  setShowLyrics,
  setUserInputtingText,
  selectSoloMode,
  setSoloMode,
  setOfflineModalVisible,
} from "../../features/ui/uiSlice";

import {
  selectLyricsLyrics
} from "../../features/lyrics/lyricsSlice";
import { useNavigate } from "react-router-dom";

// pgw 30-sep-2025 - replace all debugging debugLog calls with debugLog
import { debugLog } from '../../debugLog';

// pgw 01-may-2026
import { isCgTestingEnabled } from "../../utils/cgTesting";


import {
  isUgcTestingEnabled,
  updateUgcSongInLibrary,
  uploadUgcLyricsJson,
} from "../../utils/ugcTesting";


import {
  fetchCgSetlistContents,
  getCgLyricsFileUrl,
  getCgChaptersFileUrl,
  fetchCgTextFile,
  parseCgChaptersText,
  mapCgMusicItemToLibrarySong,
} from "../../utils/cgTesting";

import { resetLyrics } from "../../features/lyrics/lyricsSlice";
import { fetchLyrics } from "../../features/lyrics/lyricsAPI";
import { removeAssetFromCache } from "../../services/assetStore";

import { updateSetlistItemMetadata } from "../../features/setlists/setlistsSlice";

import ReactDOM from "react-dom";
import { Rnd } from "react-rnd";

let mountedOnce = false;

const shouldShowMetadataEditor = () =>
  isCgTestingEnabled() || isUgcTestingEnabled();


// pgw 09-feb-2026
let doprint = false;
// let doprint = true;


const METADATA_PANEL_BOUNDS_KEY = "choirplayer:metadataEditorPanelBounds";



function getSavedMetadataPanelBounds() {
  try {
    const saved = window.localStorage.getItem(METADATA_PANEL_BOUNDS_KEY);
    return saved
      ? JSON.parse(saved)
      : { x: 0, y: 58, width: 673, height: 552 };
  } catch (e) {
    return { x: 0, y: 58, width: 673, height: 552 };
  }
}



function saveMetadataPanelBounds(bounds) {
  if (doprint) console.log("metadata editor panel bounds =", bounds);
  window.localStorage.setItem(METADATA_PANEL_BOUNDS_KEY, JSON.stringify(bounds));
}


const METADATA_EDITOR_COLUMNS_KEY = "choirplayer:metadataEditorColumnWidths";


function getSavedMetadataEditorColumnWidths() {
  const defaults = { name: 433, time: 85, goto: 50, delete: 70 };

  try {
    const saved = window.localStorage.getItem(METADATA_EDITOR_COLUMNS_KEY);
    const parsed = saved ? JSON.parse(saved) : {};

    return {
      ...defaults,
      ...parsed,

      // force sane minimums even if old small values were saved
      name: Math.max(Number(parsed.name || defaults.name), 433),
      time: Math.max(Number(parsed.time || defaults.time), 85),
      goto: Math.max(Number(parsed.goto || defaults.goto), 50),
      delete: Math.max(Number(parsed.delete || defaults.delete), 70),
    };
  } catch (e) {
    return defaults;
  }
}



function saveMetadataEditorColumnWidths(widths) {
  if (doprint) console.log("metadata editor column widths =", widths);
  window.localStorage.setItem(METADATA_EDITOR_COLUMNS_KEY, JSON.stringify(widths));
}



const TourGuideContainer = ({title, text}) => {

  return <div className="flex flex-col justify-center align-middle">
    <h3 className="text-lg font-bold mb-2 text-primary">{title}</h3>
    <p>{text}</p>
    {/* <Button style={{width: 100}}>Close</Button> */}
    </div>

}

const steps = [
  {
    selector: ".stem",
    content: <TourGuideContainer
      title="Song stem"
      text="This is the stem content. You can change the volume of each part by tapping the group. As you turn down the stem, it will fade out to grey."
    />,
  },
  {
    selector: "#transport",
    content: <TourGuideContainer
      title="Playback controls"
      text="Use these to control playback of the song"
    />,
  },
  {
    selector: "#section-button",
    content: <TourGuideContainer
      title="Song sections"
      text="Skip between different song sectios with this button"
    />,
  },
  {
    selector: "#solo-button",
    content: <TourGuideContainer
    title="Playback controls"
    text="Clicking this will enable solo mode, which allows you to listen to just one part at a time. Click the section you'd like to solo"
  />
  },
  {
    selector: "#lyrics-button",
    content: <TourGuideContainer
    title="Show/Hide Lyrics"
    text="View the songs lyrics while you listen to the song."
  />,
  },
  {
    selector: "#pitch-speed-button",
    content: <TourGuideContainer
    title="Speed/Pitch"
    text="You can change the speed and pitch of the playback independently"
  />,
  },
  {
    selector: "#reset-button",
    content: <TourGuideContainer
    title="Reset mix"
    text="If you'd like to reset everything back to the original settings, then tap reset!"
  />,
  },
  
  
  
];


const Session = () => {
  const dispatch = useDispatch();
  const {setSteps, setIsOpen} = useTour();
  // const session = useSelector(selectSession);
  // const sessions = useSelector(selectSession);
  const status = useSelector(selectStatus);
  const stems = useSelector(selectStems);
  const userStems = useSelector(selectUserStems);
  const duration = useSelector(selectDuration);
  const loadingProgress = useSelector(selectLoadingProgress);
  const soloMode = useSelector(selectSoloMode);

  const introLevel = useSelector(selectIntroLevel);
  const click1Level = useSelector(selectClick1Level);
  const click2Level = useSelector(selectClick2Level);
  const cueLevel = useSelector(selectCueLevel);

  const setlistSessions = useSelector(selectSetlistSessions);

  const newTrackModalVisible = useSelector(selectNewTrackModalVisible);

  const clientConfiguration = useSelector(selectClientConfig);

  const currentLyrics = useSelector(selectLyricsLyrics);

  const showLyrics = useSelector(selectShowLyrics);
  const lyricsStatus = useSelector(selectLyricsStatus);

  const [pendingNewChannel, setPendingNewChannel] = useState(false);

  const [lrcLyrics, setLrcLyrics] = useState("");
  const fetchedLyricsUrlRef = useRef("");


  const [pendingNewChannelName, setPendingNewChannelName] = useState("");
  const [pendingDeleteChannel, setPendingDeleteChannel] = useState(false);

  const [showHeadphoneWarning, setShowHeadphoneWarning] = useState(false);
  const [showRecordArmWarning, setShowRecordArmWarning] = useState(false);

  // pgw 01-may-2026
  const [metadataEditorOpen, setMetadataEditorOpen] = useState(false);


  const setMetadataEditorOpenState = (open) => {
	window.__CHOIRPLAYER_METADATA_EDITOR_OPEN__ = open;
	document.body.dataset.choirplayerMetadataEditorOpen = open ? "1" : "0";
	setMetadataEditorOpen(open);
  };


  useEffect(() => {
	window.__CHOIRPLAYER_METADATA_EDITOR_OPEN__ = metadataEditorOpen;
	document.body.dataset.choirplayerMetadataEditorOpen = metadataEditorOpen ? "1" : "0";

	return () => {
	  window.__CHOIRPLAYER_METADATA_EDITOR_OPEN__ = false;
	  document.body.dataset.choirplayerMetadataEditorOpen = "0";
	};
  }, [metadataEditorOpen]);


  const [chapterEditorRows, setChapterEditorRows] = useState([]);
  const [lyricsEditorRows, setLyricsEditorRows] = useState([]);
  const [newChapterName, setNewChapterName] = useState("");
  const [newLyricText, setNewLyricText] = useState("");

  const [chapterRows, setChapterRows] = useState([]);
  const [lyricRows, setLyricRows] = useState([]);

  const [metadataButtonSlot, setMetadataButtonSlot] = useState(null);


  const [metadataPanelBounds, setMetadataPanelBounds] = useState(getSavedMetadataPanelBounds);
  const [metadataColumnWidths, setMetadataColumnWidths] = useState(getSavedMetadataEditorColumnWidths);


  const [currentTimeState, setCurrentTimeState] = useState(0);
  const navigate = useNavigate();


	const session = useSelector(state => state.session);


	useEffect(() => {
	  if (!shouldShowMetadataEditor()) return;

	  const slot = document.getElementById("cg-metadata-buttons-slot");
	  setMetadataButtonSlot(slot);
	}, [status, mountedOnce]);


	useEffect(() => {
	  if (!session?.id) return;

	  if (doprint) console.log("[SESSION DEBUG] after open (redux)", {
		id: session.id,
		speed: session.speed,
		pitch: session.pitch,
		stems: session.stems?.map(s => ({ id: s.id, level: s.level, muted: s.muted, soloed: s.soloed }))
	  });
	}, [session?.id]);





	useEffect(() => {
	  if (!session?.id) return;

	  if (doprint) console.log("[SESSION PAGE] session opened", {
		id: session.id,
		songId: session.song?.songId,
		origin: session.origin,
		stems: session.stems?.map(s => ({
		  id: s.id,
		  level: s.level,
		  muted: s.muted,
		  soloed: s.soloed
		}))
	  });
	}, [session?.id]);



	// pgw 26-jan-2026
	useEffect(() => {
	  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] stems now", stems.map(s => ({id:s.id, level:s.level})));
	}, [stems]);



	useEffect(()=> {
	  const lyricsUrl = session.song?.lyricsUrl || session.song?.lyrics || "";
	  const lyricsFetchKey = `${session.id || ""}|${session.song?.songId || ""}|${lyricsUrl}`;

	  if (doprint) {
		  console.log("[UGC] Session lyrics fetch check =", {
			sessionId: session.id,
			songId: session.song?.songId,
			lyricsUrl,
			lyricsFetchKey,
			lastFetchedLyricsKey: fetchedLyricsUrlRef.current,
			lyricsStatus,
		  });
	  }

	  if (!lyricsUrl) {
		return;
	  }

	  if (fetchedLyricsUrlRef.current === lyricsFetchKey) {
		if (doprint) console.log("[UGC] skipping lyrics fetch; already fetched for this session/song/url");
		return;
	  }

	  fetchedLyricsUrlRef.current = lyricsFetchKey;

	  if (doprint) console.log("[UGC] clearing old lyrics and fetching fresh lyrics from file =", lyricsUrl);
	  dispatch(resetLyrics());
	  dispatch(fetchLyricsAsync(lyricsUrl));
	}, [
	  dispatch,
	  session.id,
	  session.song?.songId,
	  session.song?.lyricsUrl,
	  session.song?.lyrics,
	])



  // pgw 20-jan-2026
  // Run this block of code every time status changes
  // So nothing inside here runs on every render — only when status gets a new value.
  useEffect(() => {

		// pgw testing 19-jan-2025
        debugLog('pgw player-application/src/containers/Session/index.js pgw2_5 - p1');
		debugLog("pgw2_5 - p2, status = ", status);
		

		// debugLog(status);


      if (status === 'ready') {
        if (localStorage) {
          if (!localStorage.getItem('show_session_tour_guide')) {

			// pgw 19-jun-2025 - do not show tour guide for now...
// 			localStorage.setItem('show_session_tour_guide', 'true');
//             setIsOpen(true);

            localStorage.setItem('show_session_tour_guide', 'false');
            setIsOpen(false);


          }
        }
      }


	  if (status === "unmounted" || (status !== "empty" && !mountedOnce)) {
		dispatch(stopSong());
		dispatch(mountSessionAsync({ cache: clientConfiguration.features.session.cacheOnMount }));
		dispatch(setSoloMode(false));

		// Only do this for truly empty sessions, not restored sessions
		if (status === "empty") {

		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] status is empty - calling turnUpAllStems" )

		  if (doprint) console.log("[SESSION] about to turnUpAllStems", {
			status,
			mountedOnce,
			stems: session?.stems?.map(s => ({ id: s.id, level: s.level }))
		  });

		  const isRestoredSession = !!session?.origin; // library or setlist
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] status is empty -  isRestoredSession = " , isRestoredSession)

		  if (!isRestoredSession && status === "empty") {
		    if (doprint) console.log("[CONTAINERS_SESSION_INDEX] status is empty - calling turnUpAllStems " )
			turnUpAllStems();
		  }


		} else {
		  if (doprint) console.log("[SESSION] skipping turnUpAllStems; preserving restored mixer");
		}
	  }


      if (status === "error") {
		console.trace("error");
        dispatch(setOfflineModalVisible(true))
      }

      mountedOnce = true;
  }, [status]);

  useEffect(() => {
    
    setShowLyrics(false)
    dispatch(resetLastCreated());
    
	// pgw 01-may-2026
    setLrcLyrics("");
    // setLrcLyrics(null);
    // if (soloMode) {
    //   turnUpAllStems();
    //   dispatch(setSoloMode(false));
    // }
    

    setSteps(steps);
    
    return () => {
      setSteps([]);
      setIsOpen(false);
      dispatch(setShowLyrics(false));
      dispatch(stopSong());

	if (doprint) console.log("[CONTAINERS_SESSION_INDEX] useEffect return p1 session = ", session)


    };
  }, []);


// pgw 24-jan-2026
// isInputChannel is defined and set only for channels that are used for recording.
// So, for now, ignore it
// Note that it is passed to changeChannelGain() as userStem
// In changeChannelGain, userstem is tested and, if not defined, the stem is found using the index argument
// In other words, userstem indicates it's a stem created by the user, which can be used for recording.

  const updateChannelGain = (channelId, value) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      changeChannelGain({
        index: channel.index,
        value,
        userStem: channel.isInputChannel,
      })
    );

	// pgw 07-dec-2025

	if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - calling saveExistingSession, session = ", session)

	if (session.song?.mix) {
		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - calling saveExistingSession, session.song?.mix = ", session.song?.mix)
		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - calling saveExistingSession, session.song.mix = ", session.song.mix)

	}
	else {
		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - NOT calling saveExistingSession - no song " )
	}

  };


const updateChannelGainOld2 = (channelId, value) => {

  // pgw debug
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[0] = ", stems[0]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[1] = ", stems[1]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[2] = ", stems[2]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[3] = ", stems[3]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[4] = ", stems[4]);
// isInputChannel


  const channel = stems
    .concat(userStems)
    .find((channel) => channelId === channel.id);

  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - channel = ", channel);


  if (!channel) return;

  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - channel.isInputChannel = ", channel.isInputChannel);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - channel.channel.muted = ", channel.channel.muted);


  dispatch(
    changeChannelGain({
      index: channel.index,
      value,
      userStem: channel.isInputChannel,
    })
  );

  // Build next stems arrays for saving (session/stems are stale in this tick)
  const patch = (arr) =>
    arr.map((s) =>
      s.index === channel.index
        ? { ...s, level: value }
        : s
    );

  const nextStems = channel.isInputChannel ? stems : patch(stems);
  const nextUserStems = channel.isInputChannel ? patch(userStems) : userStems;

  const nextSession = {
    ...session,
    stems: nextStems,
    userStems: nextUserStems,
    lastUpdatedAt: new Date().toISOString(),
  };

  // pgw 07-dec-2025
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - saving nextSession", {
    channelId,
    index: channel.index,
    value,
  });

  if (session.song?.mix) {
    if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - session.song?.mix = ", session.song?.mix);

		// pgw 26-jan-2026
	/*
	
    if (session?.song?.mix?.includes("Test Song")) {
      if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain mix contains 'Test Song' - dispatch(saveExistingSession) ");
      dispatch(saveExistingSession(nextSession));
    } else {
      if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain mix does NOT contain 'Test Song'");
    }
	*/

  } else {
    if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - no song");
  }
};


// pgw 26-jan-2026
// Added dispatch(saveExistingSession(nextSession))

const updateChannelGainOld3 = (channelId, value) => {

  // pgw debug
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[0] = ", stems[0]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[1] = ", stems[1]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[2] = ", stems[2]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[3] = ", stems[3]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - stems[4] = ", stems[4]);

  const channel = stems.concat(userStems).find((c) => channelId === c.id);

  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - channel = ", channel);

  if (!channel) return;

  // ChoirPlayer: no input channels → always stems, never userStems
  dispatch(
    changeChannelGain({
      index: channel.index,
      value,
      userStem: false,
    })
  );

  // Build the next session snapshot (since session/stems are stale in this tick)
  const nextStems = stems.map((s) =>
    s.index === channel.index ? { ...s, level: value } : s
  );

  const nextSession = {
    ...session,
    stems: nextStems,
    lastUpdatedAt: new Date().toISOString(),
  };

  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelGain - saving nextSession", {
    id: channel.id,
    index: channel.index,
    value,
  });

};




  const updateChannelPan = (channelId, value) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      changeChannelPan({
        index: channel.index,
        value,
        userStem: channel.isInputChannel,
      })
    );
  };

  const updateChannelMuteOld = (channelId) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      channel.muted
        ? unmuteChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
        : muteChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
    );

	// pgw 07-dec-2025
	if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - calling saveExistingSession, session = ", session)

	if (session.song?.mix) {
		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - calling saveExistingSession, session.song?.mix = ", session.song?.mix)
		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - calling saveExistingSession, session.song.mix = ", session.song.mix)

	}
	else {
		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - NOT calling saveExistingSession - no song " )
	}

  };



// If you paste the shape of one stem object (from console.log(stems[0])) and what isInputChannel actually means in your app, 
// I’ll adjust the patch to be 100% correct for your data model.

// pgw 26-jan-2026
// ChoirPlayer - doesn't seem to be called !!!
const updateChannelMute = (channelId) => {

  // pgw debug
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - stems[0] = ", stems[0]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - stems[1] = ", stems[1]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - stems[2] = ", stems[2]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - stems[3] = ", stems[3]);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - stems[4] = ", stems[4]);
// isInputChannel

  const channel = stems.concat(userStems).find((c) => channelId === c.id);

  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - channel = ", channel);

  if (!channel) return;


  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - channel.isInputChannel = ", channel.isInputChannel);
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - channel.channel.muted = ", channel.channel.muted);


  const nextMuted = !channel.muted;

  // 1) Update the audio/session slice as before
  dispatch(
    nextMuted
      ? muteChannel({ index: channel.index, userStem: channel.isInputChannel })
      : unmuteChannel({ index: channel.index, userStem: channel.isInputChannel })
  );

  // 2) ALSO build the *next* stems arrays for saving (because session in this tick is stale)
  const patch = (arr) =>
    arr.map((s) =>
      s.index === channel.index
        ? { ...s, muted: nextMuted }
        : s
    );

  const nextStems = channel.isInputChannel ? stems : patch(stems);
  const nextUserStems = channel.isInputChannel ? patch(userStems) : userStems;

  const nextSession = {
    ...session,
    stems: nextStems,
    userStems: nextUserStems,
    lastUpdatedAt: new Date().toISOString(),
  };

  // pgw debug
  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - saving nextSession muted change", {
    channelId,
    index: channel.index,
    nextMuted,
  });

  dispatch(saveExistingSession(nextSession));
};


  const updateChannelSolo = (channelId, solo) => {
    if (soloMode) {
      const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);

      dispatch(
        changeChannelGain({
          index: channel.index,
          value: 1,
          userStem: channel.isInputChannel,
        })
      );


      // pgw 07-dec-2025
	  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - calling saveExistingSession 1, session = ", session)

	  if (session.song?.mix) {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - calling saveExistingSession, session.song?.mix = ", session.song?.mix)
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - calling saveExistingSession, session.song.mix = ", session.song.mix)

		// pgw 26-jan-2026
		/*

		  if (session?.song?.mix?.includes("Test Song")) {
			  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo mix contains 'Test Song' - calling dispatch(saveExistingSession) ");
			  dispatch(saveExistingSession({
				...session,
				lastUpdatedAt: new Date().toISOString()
			  }));
		  } else {
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo mix does NOT contain 'Test Song'");
		  }
		*/

	  }
	  else {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - NOT calling saveExistingSession - no song " )
	  }

      const otherChannels = stems
      .concat(userStems)
      .filter((channel) => channelId !== channel.id);
      for (const otherChannel of otherChannels) {

        dispatch(
              changeChannelGain({
                index: otherChannel.index,
                value: 0,
                userStem: otherChannel.isInputChannel,
        }));



		if (session.song?.mix) {
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - calling saveExistingSession 2, session.song?.mix = ", session.song?.mix)
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - calling saveExistingSession2 , session.song.mix = ", session.song.mix)

		// pgw 26-jan-2026
		/*

			if (session?.song?.mix?.includes("Test Song")) {
				if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo mix contains 'Test Song' - calling dispatch(saveExistingSession) ");
				dispatch(saveExistingSession({
				  ...session,
				  lastUpdatedAt: new Date().toISOString()
				}));
			} else {
			  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo mix does NOT contain 'Test Song'");
			}
		*/

		}
		else {
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelSolo - NOT calling saveExistingSession - no song " )
		}

      }
    } else {
      const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
      dispatch(
        channel.soloed
          ? unsoloChannel({
              index: channel.index,
              userStem: channel.isInputChannel,
            })
          : soloChannel({
              index: channel.index,
              userStem: channel.isInputChannel,
            })
      );

	  // pgw 07-dec-2025
	  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - calling saveExistingSession, session = ", session)

	  if (session.song?.mix) {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - calling saveExistingSession 2, session.song?.mix = ", session.song?.mix)
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - calling saveExistingSession2 , session.song.mix = ", session.song.mix)

		// pgw 26-jan-2026
		/*

		  if (session?.song?.mix?.includes("Test Song")) {
			  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute mix contains 'Test Song' - calling dispatch(saveExistingSession) ");
			  dispatch(saveExistingSession({
				...session,
				lastUpdatedAt: new Date().toISOString()
			  }));
		  } else {
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute mix does NOT contain 'Test Song'");
		  }

		*/
	  }
	  else {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] updateChannelMute - NOT calling saveExistingSession - no song " )
	  }



    }
  };

  const updateChannelArm = async (channelId) => {
    await dispatch(attachUserMicrophoneAsync());
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    dispatch(
      channel.armed
        ? disarmChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
        : armChannel({
            index: channel.index,
            userStem: channel.isInputChannel,
          })
    );
  };

  const addStem = async () => {
    dispatch(setNewTrackModalVisible(true));
    dispatch(setNewTrackModalVisible(true));
  };

  const confirmAddStem = async () => {
    if (pendingNewChannelName) {
      await dispatch(attachUserMicrophoneAsync());
      dispatch(addNewStem({ name: pendingNewChannelName }));
      setPendingNewChannelName("");
      dispatch(setNewTrackModalVisible(false));

	  // pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
      /*
		dispatch(
        addNotificationAsync({
          title: "Track added to session",
          text: `${pendingNewChannelName} added to the session
        `,
          type: "positive",
        })
      );
		*/

    }
  };

  const shareSession = async () => {
    await dispatch(packSessionAsync());

    // pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
	/*
    dispatch(
      addNotificationAsync({
        title: "Session downloaded",
        text: `The session was packed up into a .session file and downloaded`,
        type: "positive",
      })
    );
	*/

  };

  const downloadSession = async () => {
    await dispatch(saveExistingSession(session));
    await dispatch(mountSessionAndRenderOfflineAsync({cache:clientConfiguration.features.session.cacheOnMount}));
	
	// pgw 13-jul-2025 remove notifications, as they mess with setlists and can break player rendering
	/*
    dispatch(
      addNotificationAsync({
        title: "Session downloaded",
        text: `The session mix was bounced down and downloaded.`,
        type: "positive",
      })
    );
	*/

  };

  const resetSession = async () => {
    await dispatch(resetSessionMixerSettings());


    dispatch(
      addNotificationAsync({
        title: "Session reset",
        text: `All the mixer settings for the session were reset.`,
        type: "positive",
      })
    );

  };

  const deleteStem = async (channelId) => {
    const channel = stems
      .concat(userStems)
      .find((channel) => channelId === channel.id);
    if (channel.isInputChannel) {
      setPendingDeleteChannel(channel);
    }
  };

  const confirmDeleteTrack = async () => {
    await dispatch(removeChannelAsync(pendingDeleteChannel));
    setPendingDeleteChannel(false);
  };

  const handleCancel = async () => {
    await dispatch(unmountSessionAsync());
	// pgw 29-sep-2025
	debugLog('player-application/src/containers/Session/index.js handleCancel navigating to /library');
    navigate('/library');
    setTimeout(()=>window.location.reload(),100);
  }

  const formLrcLyrics = (lyricsArray) => {
	let lrcString = '';

	for (const line of (lyricsArray || [])) {
	  lrcString = lrcString + `[${new Date(1000 * line.time).toISOString().substr(14, 8)}] ${line.text || ""}\n`;
	}

	return lrcString;
  }

  // pgw 01-may-2026
  useEffect(()=>{
    if (Array.isArray(session.song?.lyricsOverride) && session.song.lyricsOverride.length > 0) {

	  if (doprint) {
		  console.log("[UGC] applying lyricsOverride to lrcLyrics", {
			songId: session.song?.songId,
			count: session.song.lyricsOverride.length,
		  });
	  }


      setLrcLyrics(formLrcLyrics(session.song.lyricsOverride));
    } else if (Array.isArray(currentLyrics) && currentLyrics.length > 0) {

	  if (doprint) {
		  console.log("[UGC] applying fetched lyrics to lrcLyrics", {
			songId: session.song?.songId,
			count: currentLyrics.length,
		  });
	  }

      setLrcLyrics(formLrcLyrics(currentLyrics));
    }
  },[currentLyrics, session.song?.lyricsOverride, session.song?.songId])



  useEffect(()=>{
    const currentTimeInterval = setInterval(()=>{
      setCurrentTimeState(audioEngine.currentPosition);
    },100);
    return ()=>{
      
      
      dispatch(setSoloMode(false));
      // turnUpAllStems()
      clearInterval(currentTimeInterval)
    }
  },[])

  const turnDownAllStems = () => {
    for (const stem of stems.concat(userStems)) {

      dispatch(
        changeChannelGain({
          index: stem.index,
          value: 0,
          userStem: stem.isInputChannel,
        })
      );

	  // pgw 07-dec-2025
	  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnDownAllStems - calling saveExistingSession, session = ", session)

	  if (session.song?.mix) {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnDownAllStems - calling saveExistingSession 2, session.song?.mix = ", session.song?.mix)
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnDownAllStems - calling saveExistingSession2 , session.song.mix = ", session.song.mix)

		// pgw 26-jan-2026
		/*

		  if (session?.song?.mix?.includes("Test Song")) {
			  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnDownAllStems mix contains 'Test Song' - calling dispatch(saveExistingSession) ");
			  dispatch(saveExistingSession({
				...session,
				lastUpdatedAt: new Date().toISOString()
			  }));
		  } else {
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnDownAllStems mix does NOT contain 'Test Song'");
		  }

		*/
	  }
	  else {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnDownAllStems - NOT calling saveExistingSession - no song " )
	  }


    }
  }

  const turnUpAllStems = () => {

// 	const hasCustomMix1 = session?.stems?.some(s => s.level !== 1 || s.muted || s.soloed);
// 	const hasCustomMix2 = session?.stems?.some(s => s.level !== 0.8 || s.muted || s.soloed);
// 	if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems, hasCustomMix1 = ", hasCustomMix1)
// 	if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems, hasCustomMix2 = ", hasCustomMix2)

	if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems, userStems = ", userStems)

    for (const stem of stems.concat(userStems)) {

      dispatch(
        changeChannelGain({
          index: stem.index,
          value: 1,
          userStem: stem.isInputChannel,
        })
      );




	  // pgw 07-dec-2025
	  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems - calling saveExistingSession, session = ", session)


	  if (session.song?.mix) {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems - calling saveExistingSession 2, session.song?.mix = ", session.song?.mix)
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems - calling saveExistingSession2 , session.song.mix = ", session.song.mix)

		// pgw 26-jan-2026
		/*

		  if (session?.song?.mix?.includes("Test Song")) {
			  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems mix contains 'Test Song' - calling dispatch(saveExistingSession) ");
			  dispatch(saveExistingSession({
				...session,
				lastUpdatedAt: new Date().toISOString()
			  }));
		  } else {
			if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems mix does NOT contain 'Test Song'");
		  }
	*/

	  }
	  else {
		  if (doprint) console.log("[CONTAINERS_SESSION_INDEX] turnUpAllStems - NOT calling saveExistingSession - no song " )
	  }



    }
  }

  useEffect(()=>{
    if (soloMode) {
     
      //turn down level of all channels
      // turnDownAllStems()

		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] useEffect soloMode is true - calling turnUpAllStems" )
		if (doprint) console.log("[SESSION] soloMode - about to turnUpAllStems");

      turnUpAllStems()
    } else {

		if (doprint) console.log("[CONTAINERS_SESSION_INDEX] useEffect soloMode is NOT true - doing nothing" )

      // setIsOpen(false);
      //unmute all channels
      // turnUpAllStems()
    }
  },[soloMode])


  // pgw 01-may-2026
  const normaliseChapterRows = (rows) => {
	return (rows || [])
	  .map((row) => ({
		name: String(row.name || "").trim(),
		time: Number(row.time || 0),
	  }))
	  .filter((row) => row.name)
	  .sort((a, b) => a.time - b.time);
  };

  const normaliseLyricRows = (rows) => {
	return (rows || [])
	  .map((row) => ({
		text: String(row.text || row.name || "").trim(),
		time: Number(row.time || 0),
	  }))
	  .filter((row) => row.text)
	  .sort((a, b) => a.time - b.time);
  };


  const openMetadataEditor = async () => {
	const chapters = session.song?.chapters || [];
	let lyrics = session.song?.lyricsOverride || currentLyrics || [];

	if (isCgTestingEnabled() && (!Array.isArray(lyrics) || lyrics.length === 0)) {
	  const lyricsUrl = session.song?.lyricsUrl || session.song?.lyrics || "";

	  if (doprint) {

		  console.log("[CG] openMetadataEditor loading lyrics for editor =", {
			songId: session.song?.songId,
			lyricsUrl,
		  });
	  }

	  if (lyricsUrl) {
		try {
		  const fetchedLyrics = await fetchLyrics(lyricsUrl, () => {});
		  lyrics = fetchedLyrics?.lyrics || [];

	  	  if (doprint) {

			  console.log("[CG] openMetadataEditor loaded lyrics into editor =", {
				songId: session.song?.songId,
				lyricsCount: lyrics.length,
				lyrics,
			  });
		  }


		} catch (e) {
		  console.error("[CG] openMetadataEditor could not load lyrics", e);
		}
	  }
	}

	setChapterEditorRows(normaliseChapterRows(chapters));
	setLyricsEditorRows(normaliseLyricRows(lyrics));

	setNewChapterName("");
	setNewLyricText("");


	setMetadataEditorOpenState(true);

  };

  const addChapterAtCurrentTime = () => {
	const name = newChapterName.trim();
	if (!name) return;

	const nextRows = normaliseChapterRows([
	  ...chapterEditorRows,
	  {
		name,
		time: Number(((audioEngine.currentPosition || 0) / 1000).toFixed(3)),
	  },
	]);

	setChapterEditorRows(nextRows);
	autosaveMetadataEditor(nextRows, lyricsEditorRows);

	setNewChapterName("");
  };

  const addLyricAtCurrentTime = () => {
	const text = newLyricText.trim();
	if (!text) return;

	const nextRows = normaliseLyricRows([
	  ...lyricsEditorRows,
	  {
		text,
		time: Number(((audioEngine.currentPosition || 0) / 1000).toFixed(3)),
	  },
	]);

	setLyricsEditorRows(nextRows);
	autosaveMetadataEditor(chapterEditorRows, nextRows);

	setNewLyricText("");
  };

  const updateChapterEditorRow = (index, field, value) => {
	setChapterEditorRows((rows) =>
	  rows.map((row, ix) =>
		ix === index
		  ? { ...row, [field]: field === "time" ? Number(value) : value }
		  : row
	  )
	);
  };

  const updateLyricEditorRow = (index, field, value) => {
	setLyricsEditorRows((rows) =>
	  rows.map((row, ix) =>
		ix === index
		  ? { ...row, [field]: field === "time" ? Number(value) : value }
		  : row
	  )
	);
  };

  const deleteChapterEditorRow = (index) => {
	const nextRows = chapterEditorRows.filter((row, ix) => ix !== index);
	setChapterEditorRows(nextRows);
	autosaveMetadataEditor(nextRows, lyricsEditorRows);
  };

  const deleteLyricEditorRow = (index) => {
	const nextRows = lyricsEditorRows.filter((row, ix) => ix !== index);
	setLyricsEditorRows(nextRows);
	autosaveMetadataEditor(chapterEditorRows, nextRows);
  };


  const gotoMetadataTime = (time) => {
	const seconds = Number(time || 0);

	dispatch(seek(seconds * 1000));
	dispatch(pauseSong());
  };


  const saveUgcChaptersToSonglist = async () => {
    try {
      const chapters = normaliseChapterRows(chapterEditorRows);

      const updatedSong = await updateUgcSongInLibrary(session.song.songId, {
        chapters,
      });

      dispatch(updateSessionMetadata({
        chapters,
        songPatch: {
          chapters,
          lyricsUrl: updatedSong?.lyricsUrl || session.song?.lyricsUrl || "",
        },
      }));

      alert("Chapters saved to UGC songlist.");
    } catch (e) {
      console.error("Could not save chapters to UGC songlist", e);
      alert("Could not save chapters to UGC songlist.");
    }
  };



  const saveUgcLyricsToSonglist = async () => {
	try {
	  const lyrics = normaliseLyricRows(lyricsEditorRows);

	  const lyricsUrl = await uploadUgcLyricsJson(
		session.song.songId,
		lyrics
	  );

	  const updatedSong = await updateUgcSongInLibrary(
		session.song.songId,
		{
		  lyricsUrl,
		}
	  );

	  dispatch(updateSessionMetadata({
		lyrics,
		songPatch: {
		  lyricsUrl: updatedSong?.lyricsUrl || lyricsUrl,
		},
	  }));

	  setLrcLyrics(formLrcLyrics(lyrics));

	  alert("Lyrics uploaded and saved to UGC songlist.");
	} catch (e) {
	  console.error("Could not upload lyrics to UGC songlist", e);
	  alert("Could not upload lyrics to UGC songlist.");
	}
  };


  const autosaveMetadataEditor = (nextChapters = chapterEditorRows, nextLyrics = lyricsEditorRows) => {
	try {
	  const chapters = normaliseChapterRows(nextChapters);
	  const lyrics = normaliseLyricRows(nextLyrics);

	  saveMetadataOverride(session.song.songId, {
		chapters,
		lyrics,
	  });

	  dispatch(updateSessionMetadata({
		chapters,
		lyrics,
	  }));

	  setLrcLyrics(formLrcLyrics(lyrics));
	} catch (e) {
	  console.error("Could not autosave metadata.", e);
	}
  };


  const saveMetadataEditor = () => {
	try {
	  const chapters = normaliseChapterRows(chapterEditorRows);
	  const lyrics = normaliseLyricRows(lyricsEditorRows);

	  saveMetadataOverride(session.song.songId, {
		chapters,
		lyrics,
	  });

	  dispatch(updateSessionMetadata({
		chapters,
		lyrics,
	  }));

	  setLrcLyrics(formLrcLyrics(lyrics));
	  setMetadataEditorOpenState(false);
	} catch (e) {
	  alert("Could not save metadata.");
	  console.error(e);
	}
  };



  // pgw 01-may-2026
  const reloadCgSongMetadata = async () => {
	try {
	  const cgSetlistId = session.origin?.cgSetlistId || session.origin?.setlistId;

	  const cgMusicId = session.song?.cgMusicId;

	  if (!cgSetlistId || !cgMusicId) {
		alert("Could not reload CG metadata: missing setlist id or music id.");
		return;
	  }

	  const rawItems = await fetchCgSetlistContents(cgSetlistId, true);

	  const rawSong = rawItems.find(
		(item) => String(item.music_id) === String(cgMusicId)
	  );

	  if (!rawSong) {
		alert("Could not find this song in ChoirGenius setlist.");
		return;
	  }

	  if (doprint) {
		console.log("Reloaded CG rawSong metadata", {
			title: rawSong.title,
			duration: rawSong.duration,
			music_id: rawSong.music_id,
		  });
	  }

	  const chaptersUrl = getCgChaptersFileUrl(rawSong);
	  const lyricsUrl = getCgLyricsFileUrl(rawSong);

	  let chapters = [];

	  if (chaptersUrl) {
		const chaptersText = await fetchCgTextFile(chaptersUrl);
		chapters = parseCgChaptersText(chaptersText);
	  }


	  deleteMetadataOverride(session.song.songId);
	  dispatch(resetLyrics());

	  if (lyricsUrl) {
		await removeAssetFromCache(lyricsUrl);
	  }

	  const updatedSong = mapCgMusicItemToLibrarySong(rawSong, [], {
		chapters,
		chaptersUrl,
		lyricsUrl,
		skipMetadataOverride: true,
	  });


	  const freshTitle = String(rawSong.title || "").trim();
	  const freshDuration = Number(rawSong.duration || 0);

	  const songPatch = {

		mix: freshTitle || updatedSong.mix,
		title: freshTitle || updatedSong.title,
		len: freshDuration || updatedSong.len || 0,
		duration: freshDuration || updatedSong.duration || updatedSong.len || 0,


		stems: updatedSong.stems || [],
		tracknames: updatedSong.tracknames || [],
		tracks: updatedSong.tracks || [],


		key: rawSong.starting_key || updatedSong.key || "",

		starting_key: updatedSong.starting_key,
		mode: updatedSong.mode,
		ranges: updatedSong.ranges,
		voicing: updatedSong.voicing,
		solos: updatedSong.solos,
		accompaniment: updatedSong.accompaniment,
		language: updatedSong.language,
		reference: updatedSong.reference,
		lyricsUrl: updatedSong.lyricsUrl,
		lyricsOverride: undefined,
		cgChaptersUrl: updatedSong.cgChaptersUrl,
		cgChaptersCount: updatedSong.cgChaptersCount,
	  };


	  dispatch(updateSessionMetadata({
		chapters: updatedSong.chapters || [],
		lyrics: [],
		songPatch,
	  }));

	  dispatch(updateSetlistItemMetadata({
		setlistId: cgSetlistId,
		itemIx: session.origin?.itemIx,
		songPatch: {
		  ...songPatch,
		  chapters: updatedSong.chapters || [],
		  stems: updatedSong.stems || [],
		  tracknames: updatedSong.tracknames || [],
		  tracks: updatedSong.tracks || [],
		},
	  }));

	  setLrcLyrics("");
	  setChapterEditorRows(normaliseChapterRows(updatedSong.chapters || []));
	  setLyricsEditorRows([]);

	  if (updatedSong.lyricsUrl) {
		dispatch(fetchLyricsAsync(updatedSong.lyricsUrl));
	  }

	  if (doprint) console.log("ChoirGenius metadata reloaded.");
	} catch (e) {
	  console.error(e);
	  alert("Could not reload ChoirGenius metadata.");
	}
  };


  const startMetadataColumnResize = (column, event) => {
	event.preventDefault();
	event.stopPropagation();

	const startX = event.clientX;
	const startWidths = { ...metadataColumnWidths };

	document.body.style.cursor = "col-resize";
	document.body.style.userSelect = "none";

	const onPointerMove = (moveEvent) => {
	  const delta = moveEvent.clientX - startX;


	  const minWidths = {
		name: 250,
		time: 55,
		goto: 50,
		delete: 70,
	  };

	  const nextWidths = {
		...startWidths,
		[column]: Math.max(
		  minWidths[column] || 50,
		  Number(startWidths[column] || 0) + delta
		),
	  };

	  setMetadataColumnWidths(nextWidths);
	};

	const onPointerUp = () => {
	  document.body.style.cursor = "";
	  document.body.style.userSelect = "";

	  window.removeEventListener("pointermove", onPointerMove);
	  window.removeEventListener("pointerup", onPointerUp);

	  setMetadataColumnWidths((latest) => {
		saveMetadataEditorColumnWidths(latest);
		return latest;
	  });
	};

	window.addEventListener("pointermove", onPointerMove);
	window.addEventListener("pointerup", onPointerUp);
  };



  const metadataColumnStyle = {
	gridTemplateColumns: `${metadataColumnWidths.name}px ${metadataColumnWidths.time}px ${metadataColumnWidths.goto}px ${metadataColumnWidths.delete}px`,
  };

  const MetadataColumnResizeHandle = ({ column }) => (
	<div
	  role="separator"
	  title={`Resize ${column} column`}
	  onPointerDown={(event) => startMetadataColumnResize(column, event)}
	  style={{
		position: "absolute",
		right: "-6px",
		top: 0,
		width: "12px",
		height: "100%",
		cursor: "col-resize",
		zIndex: 9999,
		background: "rgba(59, 130, 246, 0.25)",
		borderLeft: "1px solid rgba(59, 130, 246, 0.8)",
	  }}
	/>
  );

  const renderMixer = () => {
    switch (clientConfiguration.features.session.mixerTemplate) {
      case 'choir':
        return <ChoirConductor
          showMixRecord={clientConfiguration.features.session.showMixRecord}
          showMixControls={clientConfiguration.features.session.showMixControls}
          stems={stems.concat(userStems)}
          getVuLevel={audioEngine.getChannelPeak}
          onFaderChange={updateChannelGain}
          onPanChange={updateChannelPan}
          onMuteStem={updateChannelMute}
          onSoloStem={updateChannelSolo}
          onArmStem={updateChannelArm}
          onDeleteStem={deleteStem}
          onSave={(a, b, c) => {
            // debugLog(a, b, c);
          }}
          onAddStem={addStem}
          onDownload={downloadSession}
          onShare={shareSession}
          onReset={resetSession}
          onChangeIntroLevel={(newValue) => dispatch(setIntroLevel(newValue))}
          onChangeClick1Level={(newValue) => dispatch(setClick1Level(newValue))}
          onChangeClick2Level={(newValue) => dispatch(setClick2Level(newValue))}
          onChangeCueLevel={(newValue) => dispatch(setCueLevel(newValue))}
          // onSoloModeToggle={handleSoloModeToggle}
          step={0.5}
          showGuideChannelButtons={false}
          introLevel={introLevel}
          click1Level={click1Level}
          click2Level={click2Level}
          cueLevel={cueLevel}
          lyrics={lrcLyrics || ""}
          currentTimeMilliseconds={currentTimeState}
          showLyricsButton={true}
          lyricsActive={showLyrics}
          onClickLyricsButton={()=>dispatch(setShowLyrics(!showLyrics))}
          soloMode={soloMode}
      />
        
      case 'band':
        return <MixBoard
          stems={stems.concat(userStems)}
          getVuLevel={audioEngine.getChannelPeak}
          onFaderChange={updateChannelGain}
          onPanChange={updateChannelPan}
          onMuteStem={updateChannelMute}
          onSoloStem={updateChannelSolo}
          onArmStem={updateChannelArm}
          onDeleteStem={deleteStem}
          onSave={(a, b, c) => {
            // debugLog(a, b, c);
          }}
          onAddStem={addStem}
          onDownload={downloadSession}
          onShare={shareSession}
          onReset={resetSession}
          onChangeIntroLevel={(newValue) => dispatch(setIntroLevel(newValue))}
          onChangeClick1Level={(newValue) => dispatch(setClick1Level(newValue))}
          onChangeClick2Level={(newValue) => dispatch(setClick2Level(newValue))}
          onChangeCueLevel={(newValue) => dispatch(setCueLevel(newValue))}
          step={0.5}
          showGuideChannelButtons={false}
          introLevel={introLevel}
          click1Level={click1Level}
          click2Level={click2Level}
          cueLevel={cueLevel}
        />
      default:
        return <div>No template provided</div>
    }
  }

  const getLoadingText = () => {
    switch (loadingProgress.step) {
      case 0:
        return "Booting session...";
      case 1:
        return "Downloading song";
      case 2:
        return "Downloading song";
      case 3:
        return "Loading song into memory";
      case 4:
        return "Starting engine...";
      case 5:
        return "Ready to roll...";
      case 6:
        return "Packing up session...";
      case 7:
        return "Loading offline render...";
      case 8:
        return "Rendering bounce...";
      case 9:
        return "Creating download...";
      default:
        break;
    }
  };
  // debugLog(stems);
  return status === "ready" && mountedOnce ? (
    <>
      {renderMixer()}


	  {shouldShowMetadataEditor() && metadataButtonSlot && ReactDOM.createPortal(
		<>
		  <Button onClick={openMetadataEditor} size="sm">
			Edit chapters / lyrics
		  </Button>

		  {isCgTestingEnabled() && (
			<Button onClick={reloadCgSongMetadata} size="sm">
			  Reload CG metadata
			</Button>
		  )}

		</>,
		metadataButtonSlot
	  )}


	  {metadataEditorOpen && (
		<Rnd
		  size={{
			width: metadataPanelBounds.width,
			height: metadataPanelBounds.height,
		  }}
		  position={{
			x: metadataPanelBounds.x,
			y: metadataPanelBounds.y,
		  }}


		  onDragStop={(e, d) => {
			const nextBounds = {
			  ...metadataPanelBounds,
			  x: d.x,
			  y: d.y,
			};

			setMetadataPanelBounds(nextBounds);
			saveMetadataPanelBounds(nextBounds);
		  }}


		  onResizeStop={(e, direction, ref, delta, position) => {
			const nextBounds = {
			  width: Number(ref.style.width.replace("px", "")),
			  height: Number(ref.style.height.replace("px", "")),
			  x: position.x,
			  y: position.y,
			};

			setMetadataPanelBounds(nextBounds);
			saveMetadataPanelBounds(nextBounds);
		  }}

		  minWidth={520}
		  minHeight={360}
		  bounds="window"
		  dragHandleClassName="metadata-editor-drag-handle"
		  className="z-[9999]"
		>
		  <div className="flex h-full flex-col rounded-lg border bg-white shadow-2xl">

			<div className="metadata-editor-drag-handle flex cursor-move items-center justify-between border-b bg-gray-100 px-4 py-2">
			  <div className="font-bold">
				Edit chapters / lyrics
			  </div>

			  <button
				type="button"
				className="rounded px-1 py-0 text-sm hover:bg-gray-200"
				onClick={() => setMetadataEditorOpenState(false)}
			  >
				✕
			  </button>
			</div>

			<div className="flex-1 overflow-auto p-4">
			  <div className="w-full space-y-4">

				<div>
				  <div className="flex gap-2 mb-2">
					<input
					  className="border rounded-md px-1 py-0"
					  value={newChapterName}
					  placeholder="Chapter name"
					  onChange={(e) => setNewChapterName(e.target.value)}
					/>

					<Button onClick={addChapterAtCurrentTime}>
					  Add chapter at current time
					</Button>


					<Button
					  onClick={() => {
						if (isUgcTestingEnabled()) {
						  saveUgcChaptersToSonglist();
						  return;
						}

						const chapters = normaliseChapterRows(chapterEditorRows);
						downloadTextFile(
						  `${session.song?.songId || "song"}-chapters.xml`,
						  chaptersToXml(chapters)
						);
					  }}
					>
					  {isUgcTestingEnabled() ? "Save chapters to songlist" : "Export chapters XML"}
					</Button>


				  </div>

				  <label className="block font-bold mb-1">Chapters</label>

				  <div className="border text-sm overflow-auto">

					<div className="grid bg-gray-100 font-bold" style={metadataColumnStyle}>
					  <div className="relative border px-1 py-0 text-left">
						name
						<MetadataColumnResizeHandle column="name" />
					  </div>
					  <div className="relative border px-1 py-0 text-left">
						time
						<MetadataColumnResizeHandle column="time" />
					  </div>
					  <div className="relative border px-1 py-0 text-left">
						Goto
						<MetadataColumnResizeHandle column="goto" />
					  </div>
					  <div className="relative border px-1 py-0">
						Delete
						<MetadataColumnResizeHandle column="delete" />
					  </div>
					</div>



					{chapterEditorRows.map((row, ix) => (
					  <div
						key={ix}
						className="grid"
						style={{ ...metadataColumnStyle, height: 39 }}
					  >
						<div className="border px-1 py-0">
						  <input
							className="w-full h-full"
							value={row.name}
							onChange={(e) => updateChapterEditorRow(ix, "name", e.target.value)}
							onBlur={() => autosaveMetadataEditor()}
						  />
						</div>

						<div className="border px-1 py-0">



						  <input
							className="w-full h-full"
							type="text"
							inputMode="decimal"
							value={row.time}

							onChange={(e) => {
							  const v = e.target.value;

							  // allow empty, ".", "12.", "0.5", etc.
							  if (/^\d*(\.\d*)?$/.test(v) || v === "") {
								updateChapterEditorRow(ix, "time", v);
							  }
							}}

							onBlur={() => autosaveMetadataEditor()}
						  />

						</div>

						<div className="border px-1 py-0">
						  <button type="button" onClick={() => gotoMetadataTime(row.time)}>
							Goto
						  </button>
						</div>

						<div className="border px-1 py-0">
						  <button type="button" onClick={() => deleteChapterEditorRow(ix)}>
							Delete
						  </button>
						</div>
					  </div>
					))}
				  </div>


				</div>

				<div>
				  <div className="flex gap-2 mb-2">
					<input
					  className="border rounded-md px-1 py-0"
					  value={newLyricText}
					  placeholder="Lyric text"
					  onChange={(e) => setNewLyricText(e.target.value)}
					/>

					<Button onClick={addLyricAtCurrentTime}>
					  Add lyric at current time
					</Button>


					<Button
					  onClick={() => {
						if (isUgcTestingEnabled()) {
						  saveUgcLyricsToSonglist();
						  return;
						}

						const lyrics = normaliseLyricRows(lyricsEditorRows);
						downloadTextFile(
						  `${session.song?.songId || "song"}-lyrics.xml`,
						  lyricsToXml(lyrics)
						);
					  }}
					>
					  {isUgcTestingEnabled() ? "Upload lyrics to songlist" : "Export lyrics XML"}
					</Button>


				  </div>

				  <label className="block font-bold mb-1">Lyrics</label>

				  <div className="border text-sm overflow-auto">


					<div className="grid bg-gray-100 font-bold" style={metadataColumnStyle}>
					  <div className="relative border px-1 py-0 text-left">
						name
						<MetadataColumnResizeHandle column="name" />
					  </div>
					  <div className="relative border px-1 py-0 text-left">
						time
						<MetadataColumnResizeHandle column="time" />
					  </div>
					  <div className="relative border px-1 py-0 text-left">
						Goto
						<MetadataColumnResizeHandle column="goto" />
					  </div>
					  <div className="relative border px-1 py-0">
						Delete
						<MetadataColumnResizeHandle column="delete" />
					  </div>
					</div>


					{lyricsEditorRows.map((row, ix) => (
					  <div
						key={ix}
						className="grid"
						style={{ ...metadataColumnStyle, height: 39 }}
					  >
						<div className="border px-1 py-0">
						  <input
							className="w-full h-full"
							value={row.text}
							onChange={(e) => updateLyricEditorRow(ix, "text", e.target.value)}
							onBlur={() => autosaveMetadataEditor()}
						  />
						</div>

						<div className="border px-1 py-0">

						  <input
							className="w-full h-full"
							type="text"
							inputMode="decimal"
							value={row.time}

							onChange={(e) => {
							  const v = e.target.value;

							  // allow empty, ".", "12.", "0.5", etc.
							  if (/^\d*(\.\d*)?$/.test(v) || v === "") {
								updateLyricEditorRow(ix, "time", v);
							  }
							}}

							onBlur={() => autosaveMetadataEditor()}
						  />

						</div>

						<div className="border px-1 py-0">
						  <button type="button" onClick={() => gotoMetadataTime(row.time)}>
							Goto
						  </button>
						</div>

						<div className="border px-1 py-0">
						  <button type="button" onClick={() => deleteLyricEditorRow(ix)}>
							Delete
						  </button>
						</div>


					  </div>
					))}
				  </div>



				</div>

			  </div>
			</div>

			<div className="flex justify-end gap-2 border-t bg-gray-50 px-4 py-3">

				<Button onClick={() => setMetadataEditorOpenState(false)}>
				  Done
				</Button>

			</div>

		  </div>
		</Rnd>
	  )}



      <Modal
        isOpen={pendingDeleteChannel ? true : false}
        title="Delete channel?"
        text={
          <>
            <span>
              Are you sure you'd like to remove{" "}
              <b>{pendingDeleteChannel.label}</b> from the session and all
              associated recordings that have been made on it?
            </span>
          </>
        }
        confirmLabel="Yes, delete it"
        icon={
          <ExclamationIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        closeLabel="Cancel"
        onClose={() => {
          setPendingDeleteChannel(false);
        }}
        onConfirm={confirmDeleteTrack}
      />
      <Modal
        isOpen={newTrackModalVisible}
        title="New channel"
        content={
          <div className="w-full">
            <p>Please provide a name for the new channel</p>
            <Input
              onChange={(e) => setPendingNewChannelName(e.target.value)}
              placeholder="eg Vocals"
              autofocus
              label="Name"
              size="md"
              type="text"
              className="w-full border rounded-md my-4 p-2"
              autocomplete="off"
            ></Input>
          </div>
        }
        confirmLabel="Add"
        icon={
          <MicrophoneIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        closeLabel="Cancel"
        onClose={() => {
          
          dispatch(setUserInputtingText(false));
        }}
        onConfirm={confirmAddStem}
      />
      <Modal
        isOpen={showHeadphoneWarning ? true : false}
        title="Have you got headphones plugged in?"
        text="You'll want to use headphones while recording to prevent feedback from the speakers"
        confirmLabel="Okay"
        icon={
          <MicrophoneIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        onConfirm={() => setShowHeadphoneWarning(false)}
      />
      <Modal
        isOpen={showRecordArmWarning ? true : false}
        title="No armed tracks"
        text="You'll need to add a track and record arm it to record into this session"
        confirmLabel="Okay"
        icon={
          <MicrophoneIcon
            className="h-6 w-6 text-primary"
            aria-hidden="true"
          />
        }
        onConfirm={() => setShowRecordArmWarning(false)}
      />
    </>
  ) : status === "empty" ? (
    <NoSessionLoaded setlistEnabled={clientConfiguration.features.setlist.enabled} setlistSessions={setlistSessions} />
  ) : (
    <LoadingScreen
      animationFile={clientConfiguration.features.session.loadingAnimationFile}
      title="Loading session"
      text={getLoadingText()}
      loadingProgress={loadingProgress}
      totalSteps={9}
      onCancel={handleCancel}
    />
  );
};

export default Session;
